import React from 'react';
import PropTypes from 'prop-types';
import {Checkbox, Tooltip} from 'rctui';

import icons from 'styles/icons.scss';

import I18n from '../I18n';
import Thead from './Thead';
import DateTime from '../DateTime';
import TicksGraph from '../TicksGraph';
import DictView from '../DictView';

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function toFilterableValue(value) {
  if (value instanceof Array) {
    return value.join(',');
  }

  if (value == null) {
    return '';
  }

  return String(value);
}

class Table extends React.Component {
  static defaultProps = {
    primaryKey: null,
    onSort: null,
    filter: null,
    labels: [],
    items: [],
    className: 'table'
  };

  static propTypes = {
    primaryKey: PropTypes.string,
    items: PropTypes.array
  };

  constructor(props) {
    super(props);
    this.state = {
      filter: '',
    };
    this.clickButton = this.clickButton.bind(this);
    this.filterChange = this.filterChange.bind(this);
    this.onChoiceRow = this.onChoiceRow.bind(this);
  }

  clickButton(fn, value) {
    fn(value);
  }

  filterChange(name, e) {
    const value = e.target.value;
    this.setState((prevState) => {
      let filter = prevState.filter || {};
      return {
        filter: {
          ...filter,
          [name]: value,
        },
      };
    });
  }

  onChoiceRow(primaryKey, value, checked) {
    this.props.onChoiceRow && this.props.onChoiceRow(primaryKey, checked);
  }

  getTableItem(primaryKey, label, key, item, span) {
    let value = item[label.name];
    if (label.buttons && item.NoButtons === undefined) {
      let buttons = label.buttons.map((button, btnKey) => {
        let btnClass = "btn btn-small";
        let btn;
        let href;
        if (button.icon) {
          btnClass = icons.btn + " " + icons[button.icon];
        }

        if (button.href) {
          href = button.href(primaryKey);
          btn = (
            <a
              href={'app#' + href}
              onClick={this.clickButton.bind(this, button.click, primaryKey)}
              key={btnKey}>
              <div className={btnClass}></div>
              <I18n message={button.label}/>
            </a>
          );
        } else {
          btn = (
            <button className={btnClass}
                    onClick={this.clickButton.bind(this, button.click, primaryKey)}
                    key={btnKey}>
              <I18n message={button.label}/>
            </button>
          );
        }

        if (button.tooltip) {
          btn = <Tooltip position="top" key={btnKey} tip={I18n.message(button.tooltip)}>{btn}</Tooltip>;
        }

        return btn;
      });
      return (
        <td colSpan={span} key={key} className="btn-group">{buttons}</td>
      );
    }
    if (label.type === 'DateTime') {
      return (
        <td colSpan={span} key={key}>
          <DateTime ts={value}/>
        </td>
      );
    }
    if (label.type === 'longtext') {
      return (
        <td colSpan={span} key={key}>
          <textarea defaultValue={value} disabled={true} className="full"/>
        </td>
      );
    } else if (label.type === 'Lots') {
      return (
        <td colSpan={span} key={key}>{value}</td>
      );
    } else if (label.type === 'TradeCmd') {
      return (
        <td colSpan={span} key={key}><I18n message={"tradeCmd." + value}/></td>
      );
    } else if (label.type === 'Enum') {
      return (
        <td colSpan={span} key={key}><I18n message={label.enum + "." + value}/></td>
      );
    } else if (label.type === 'Alias') {
      return (
        <td colSpan={span} key={key}><I18n message={item[label.alias]}/></td>
      );
    } else if (label.type === 'Tags') {
      return (
        <td colSpan={span} key={key}>{value.join(', ')}</td>
      );
    } else if (label.type === 'CheckBox') {
      return (
        <td colSpan={span} key={key}>
          <Checkbox checked={value} value={value} onChange={this.onChoiceRow.bind(this, primaryKey)}/>
        </td>
      );
    } else if (label.type === 'TicksGraph') {
      return <td colSpan={span} key={key}><TicksGraph {...value}/></td>;
    } else if (label.type === 'DictView') {
      return <td colSpan={span} key={key}><DictView {...value}/></td>;
    } else if (label.type === 'KeyOf') {
      return <td colSpan={span} key={key}>{label.values[value]}</td>;
    }

    if (label.name == 'Login') {
      return (
        <td colSpan={span} key={key}>
          <a href={'app#' + 'Orders/Search?Login=' + encodeURIComponent(value) + '&type=all'}>
            {value}
          </a>
        </td>
      );
    } else {
      return (
        <td colSpan={span} key={key}>{value}</td>
      );
    }
  }

  hiddenByFilter(item) {
    let i, filterField, filterRegExp, val, filterValue;
    if (this.props.filter && this.state.filter) {
      if (this.props.filter instanceof Array) {
        for (i = 0; i < this.props.filter.length; i++) {
          filterField = this.props.filter[i];
          filterValue = this.state.filter[filterField];
          if (!filterValue) {
            continue;
          }
          filterRegExp = new RegExp(escapeRegExp(filterValue), 'i');
          val = toFilterableValue(item[filterField]);
          if (!filterRegExp.test(val)) {
            return true;
          }
        }
      } else {
        filterValue = this.state.filter[this.props.filter];
        if (!filterValue) {
          return false;
        }
        filterRegExp = new RegExp(escapeRegExp(filterValue), 'i');
        val = toFilterableValue(item[this.props.filter]);
        if (!filterRegExp.test(val)) {
          return true;
        }
      }
    }

    return false;
  }

  render() {
    let fullRowItems = this.props.labels.filter(label => label.fullRow);
    let groupedItems = this.props.labels.filter(label => !label.fullRow);
    let rows;
    let rowKey = 0;
    let primaryKey;
    let cols;
    let fullIdx;
    let label;

    if (this.props.items.length === 0) {
      rows = (
        <tr key={rowKey}>
          <td colSpan={this.props.labels.length}><I18n message="no_result"/></td>
        </tr>
      );
    } else {
      rows = [];

      for (let i = 0; i < this.props.items.length; i++) {
        let item = this.props.items[i];

        if (this.hiddenByFilter(item)) {
          continue;
        }

        primaryKey = this.props.primaryKey ? item[this.props.primaryKey] : null;
        cols = groupedItems.map((currentLabel, key) => this.getTableItem(primaryKey, currentLabel, key, item, null));

        rows.push(<tr key={rowKey} data-idx={rowKey}>{cols}</tr>);
        rowKey++;

        let colsCount = cols.length;

        for (fullIdx = 0; fullIdx < fullRowItems.length; fullIdx++) {
          label = fullRowItems[fullIdx];
          rows.push(<tr key={rowKey}>{this.getTableItem(primaryKey, label, 0, item, colsCount)}</tr>);
          rowKey++;
        }
      }
    }

    return (
      <div className="table-responsive">
        <table className={this.props.className}>
          <Thead
            labels={groupedItems}
            onSort={this.props.onSort}
            sortBy={this.props.sortBy}
            filterField={this.props.filter}
            filterChange={this.filterChange}
            sortDir={this.props.sortDir}
          />
          <tbody>
          {rows}
          </tbody>
        </table>
      </div>
    );
  }
}

export default Table;



// WEBPACK FOOTER //
// ./src/js/Component/Table/Table.jsx